import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { Smartphone, MessageSquareText } from "lucide-react";

export default function QuickAccess() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, loading, setUser } = useAuth();
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [known, setKnown] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && user && user.role) navigate(`/mes-sejours?claim=1&listing=${params.get("listing") || ""}`, { replace: true });
  }, [user, loading, navigate, params]);

  const start = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/phone/start", { phone });
      setKnown(data.known); if (data.known && data.name) setName(data.name);
      setStep(2);
    } catch (e2) { setError(formatApiError(e2.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const verify = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/phone/verify", { phone, code, name });
      setUser(data.user);
      navigate(data.claimed > 0 ? "/mes-sejours" : `/mes-sejours?claim=1&listing=${params.get("listing") || ""}`, { replace: true });
    } catch (e2) { setError(formatApiError(e2.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="w-full max-w-md fade-up" data-testid="quick-access">
        <div className="flex items-center gap-2 mb-6"><Logo size={40} /><span className="font-serif text-2xl text-olive">L’Orne Park</span></div>
        <h1 className="font-serif text-4xl text-olive">Bienvenue !</h1>
        <p className="text-muted-foreground mt-1 mb-6">Accédez à votre séjour en 30 secondes : nom, téléphone, code SMS. Pas de mot de passe.</p>
        {step === 1 ? (
          <form onSubmit={start} className="space-y-4" data-testid="quick-access-start">
            <div><Label>Nom et prénom</Label><Input value={name} onChange={(e) => setName(e.target.value)} required placeholder="tels qu’indiqués sur Booking / Airbnb" className="mt-1 h-11" data-testid="qa-name" /></div>
            <div><Label>Téléphone mobile</Label><Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} required placeholder="+33 6 12 34 56 78" className="mt-1 h-11" data-testid="qa-phone" /></div>
            {error && <p className="text-sm text-destructive" data-testid="qa-error">{error}</p>}
            <Button type="submit" disabled={busy} className="w-full h-11 bg-rose-brand hover:bg-rosebrand-soft text-white" data-testid="qa-send-code">
              <Smartphone className="w-4 h-4 mr-2" /> {busy ? "Envoi..." : "Recevoir mon code par SMS"}
            </Button>
          </form>
        ) : (
          <form onSubmit={verify} className="space-y-4" data-testid="quick-access-verify">
            <p className="text-sm text-olive flex items-center gap-2"><MessageSquareText className="w-4 h-4 text-rose-brand" /> Code envoyé au <strong>{phone}</strong>{known ? ` — ravi de vous revoir${name ? `, ${name.split(" ")[0]}` : ""} !` : ""}</p>
            <div><Label>Code à 6 chiffres</Label><Input inputMode="numeric" autoFocus maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} required className="mt-1 h-12 text-center text-2xl tracking-[0.5em]" data-testid="qa-code" /></div>
            {error && <p className="text-sm text-destructive" data-testid="qa-error">{error}</p>}
            <Button type="submit" disabled={busy || code.length !== 6} className="w-full h-11 bg-olive text-white" data-testid="qa-verify">{busy ? "Vérification..." : "Accéder à mon séjour"}</Button>
            <button type="button" onClick={() => { setStep(1); setCode(""); setError(""); }} className="w-full text-xs text-muted-foreground hover:underline" data-testid="qa-back">Modifier le numéro / renvoyer un code</button>
          </form>
        )}
        <p className="text-sm text-center text-muted-foreground mt-8">Équipe ou compte avec email ? <Link to="/connexion" className="text-rose-brand font-semibold" data-testid="qa-login-link">Connexion classique</Link></p>
      </div>
    </div>
  );
}
